import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Container, useMediaQuery, Grid, Button } from "@mui/material";
import type { Metier } from "models";
import metiersService from "services/metiersService";
import PageTitle from "components/molecules/PageTitle";
import MetierCard from "components/molecules/tables/MetierCard";
import MetierDialog from "components/molecules/MetierDialog";
import { sortMetier } from "utils/metierUtil";
import AddIcon from "@mui/icons-material/Add";
import LoadingPage from "pages/LoadingPage";

function MetiersPage(): ReactNode {
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const [metiers, setMetiers] = useState<Array<Metier>>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    async function getData(): Promise<void> {
      setIsLoading(true);
      try {
        const allMetiers = await metiersService.getAll();
        setMetiers(sortMetier(allMetiers));
      } catch {
        setMetiers([]);
      } finally {
        setIsLoading(false);
      }
    }

    void getData();
  }, []);
  const onMetierChanged = useCallback(
    (metier: Metier) => {
      const newMetiers = [...metiers.filter((m) => m.id !== metier.id), metier];
      setMetiers(sortMetier(newMetiers));
    },
    [metiers]
  );
  const onMetierDelete = useCallback(
    async (metier: Metier) => {
      await metiersService.deleteById(metier.id);
      const newMetiers = metiers.filter((m) => m.id !== metier.id);
      setMetiers(sortMetier(newMetiers));
    },
    [metiers]
  );
  const [isCreationDialogOpen, setIsCreationDialogOpen] = useState(false);

  return (
    <>
      <PageTitle title="Métiers" />
      <Container maxWidth={isLargeScreen ? "lg" : "xl"} sx={{ xs: { p: 0, m: 0 } }}>
        <Grid container spacing={2} sx={{ justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={() => {
              setIsCreationDialogOpen(true);
            }}
            startIcon={<AddIcon />}>
            Ajouter un nouveau métier
          </Button>

          {isLoading ? (
            <LoadingPage />
          ) : (
            <Grid container size={12} sx={{ justifyContent: "center" }}>
              <Grid container size={{ xs: 12, md: 10, lg: 8, xl: 6 }}>
                {metiers.map((m) => (
                  <Grid key={m.id} container size={12}>
                    <MetierCard
                      key={m.id}
                      metier={m}
                      onMetierChanged={onMetierChanged}
                      // eslint-disable-next-line @typescript-eslint/no-misused-promises
                      onMetierDelete={onMetierDelete}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Container>
      {isCreationDialogOpen && (
        <MetierDialog
          open
          creation
          onMetierChanged={onMetierChanged}
          onClose={() => {
            setIsCreationDialogOpen(false);
          }}
        />
      )}
    </>
  );
}

export default MetiersPage;
